import styled from "@emotion/styled";
import { isHotkey } from "is-hotkey";
import { identity } from "lodash";
import memoizeOne from "memoize-one";
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { Field } from "react-final-form";
import scrollIntoView from "scroll-into-view-if-needed";
import { Descendant, Editor, Element, Text, Transforms } from "slate";
import { withHistory } from "slate-history";
import { ReactEditor, useSlate, withReact } from "slate-react";

import { DesignToken, FormField, RadiusToken } from "@livechat/design-system-react-components";

import { IconsEnum } from "components/icon";
import RichEditor, { RichTextArea } from "components/rich-editor";
import LinkUI, { IUpdateParams } from "components/rich-editor/link-ui";
import { PluginSpec } from "components/rich-editor/plugins";
import BlockSoftBreaksPlugin from "components/rich-editor/plugins/blockSoftBreaks";
import HTMLCopyPastePlugin from "components/rich-editor/plugins/htmlCopyPaste";
import ImagesPlugin from "components/rich-editor/plugins/images";
import LinksPlugin from "components/rich-editor/plugins/links";
import ListBreaksPlugin from "components/rich-editor/plugins/listBreaks";
import OnelineHeadersPlugin from "components/rich-editor/plugins/onelineHeaders";
import RichTextPlugin, {
  RichBlockFormatType,
  RichTextFormatType,
  isBlockActive,
  isMarkActive,
} from "components/rich-editor/plugins/richText";
import { LinkElement } from "components/rich-editor/types";
import { ToolbarIcon, ToolbarSplit } from "components/toolbar";
import ImagesIcon from "components/toolbar/ImagesIcon";
import { EventPlace } from "helpers/analytics";
import { isMac } from "helpers/isMac";
import { isMobile } from "helpers/isMobile";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { IAgent } from "store/entities/agents";
import { withVar } from "themes/helpers";

import { AgentFormSection } from "../AgentFormSection";
import { AgentFormSectionId } from "../constants";
import { LockIcon } from "../styled";

interface IProps {
  canChange: boolean;
  agentID?: string;
  overflowRef?: HTMLDivElement;
}

const SignatureTextAreaContainer = styled.div<{ disabled: boolean }>`
  min-height: 100px;
  width: 100%;
  border: 1px solid
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.borderColor
    };
  line-height: 20px;
  padding: 8px 10px;
  font-size: 15px;
  box-sizing: border-box;
  border-radius: var(${RadiusToken.Radius3});

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.disabled && "opacity: 0.6;"
  }

  #rich-text-area {
    min-height: 100px;
  }

  a {
    text-decoration: underline;
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.linkColor
    };
  }

  code {
    font-size: 14px;
  }

  ol {
    list-style: decimal;
    margin: 10px 20px;
  }

  ul {
    list-style: disc;
    margin: 10px 25px;
  }

  li {
    margin: initial;
    padding: initial;
    line-height: 1.4;
  }

  h1 {
    font-size: 32px;
    line-height: 40px;
  }

  h2 {
    font-size: 24px;
    line-height: 28px;
  }

  h3 {
    font-size: 20px;
    line-height: 24px;
  }

  h4 {
    font-size: 16px;
    line-height: 24px;
  }

  h5 {
    font-size: 15px;
    line-height: 22px;
  }

  h6 {
    font-size: 12px;
    line-height: 20px;
  }

  blockquote {
    padding: 0.5em 1em;
    background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
    margin: 0.5em 0;
  }
`;

const ToolbarContainer = styled.div`
  margin-top: 0.25em;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-self: flex-end;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    align-self: center;
  }
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const shortcutTooltip = (shortcut: string, label: string) => {
  if (isMobile(navigator.userAgent)) {
    return label;
  }

  let platformShortcut;
  if (isMac(navigator.userAgent)) {
    platformShortcut = shortcut.replace(/mod/, "⌘");
  } else {
    platformShortcut = shortcut.replace(/mod/, "Ctrl");
  }

  return `${label} (${platformShortcut})`;
};

const Toolbar: React.FC<{ onLinkInsert: React.EventHandler<React.MouseEvent>; disabled: boolean }> = ({
  onLinkInsert,
  disabled,
}) => {
  const editor = useSlate();

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const isActiveText = (format: RichTextFormatType) => isMarkActive(editor, format);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const isActiveBlock = (format: RichBlockFormatType) => isBlockActive(editor, format);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTextFormat = (format: RichTextFormatType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, [0]));
    }

    const isActive = isMarkActive(editor, format);
    if (isActive) {
      editor.removeMark(format);
    } else {
      editor.addMark(format, true);
    }
  };
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleBlockFormat = (format: RichBlockFormatType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();

    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, [0]));
    }

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (editor as any).formatBlock(format);
  };

  const mobile = false;
  const privateMode = false;

  return (
    <ToolbarContainer>
      <ToolbarIcon
        testSelector="format-bold"
        icon={IconsEnum.FormatBold}
        active={isActiveText("bold")}
        disabled={disabled}
        tooltip={shortcutTooltip("mod+B", "Bold")}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleTextFormat("bold")}
      />
      <ToolbarIcon
        testSelector="format-italic"
        icon={IconsEnum.FormatItalic}
        active={isActiveText("italic")}
        disabled={disabled}
        tooltip={shortcutTooltip("mod+I", "Italic")}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleTextFormat("italic")}
      />
      <ToolbarIcon
        testSelector="format-underline"
        icon={IconsEnum.FormatUnderline}
        tooltip={shortcutTooltip("mod+U", "Underline")}
        active={isActiveText("underline")}
        disabled={disabled}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleTextFormat("underline")}
      />
      <ToolbarIcon
        testSelector="format-strikethrough"
        icon={IconsEnum.FormatStrikethrough}
        active={isActiveText("strikethrough")}
        tooltip={shortcutTooltip("mod+Shift+X", "Strikethrough")}
        disabled={disabled}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleTextFormat("strikethrough")}
      />
      <ToolbarIcon
        testSelector="format-code"
        icon={IconsEnum.Code}
        active={isActiveText("code")}
        tooltip={shortcutTooltip("mod+Shift+C", "Code")}
        disabled={disabled}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleTextFormat("code")}
      />
      <ToolbarSplit privateMode={privateMode} />
      <ToolbarIcon
        testSelector="format-numbered-list"
        icon={IconsEnum.FormatListNumbered}
        active={isActiveBlock("numbered-list")}
        tooltip={shortcutTooltip("mod+Shift+8", "Numbered List")}
        disabled={disabled}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleBlockFormat("numbered-list")}
      />
      <ToolbarIcon
        testSelector="format-bulleted-list"
        icon={IconsEnum.FormatListBulleted}
        active={isActiveBlock("bulleted-list")}
        disabled={disabled}
        tooltip={shortcutTooltip("mod+Shift+7", "Bulleted List")}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={handleBlockFormat("bulleted-list")}
      />
      <ToolbarSplit privateMode={privateMode} />
      <ToolbarIcon
        testSelector="insert-link"
        icon={IconsEnum.Link}
        active={false}
        disabled={disabled}
        tooltip={shortcutTooltip("mod+K", "Link")}
        privateMode={privateMode}
        mobile={mobile}
        onMouseDown={onLinkInsert}
      />
      <ImagesIcon disabled={disabled} tooltip="Insert Image" privateMode={privateMode} mobile={mobile} />
    </ToolbarContainer>
  );
};

const AgentSignature: React.FC<IProps> = ({ canChange, agentID, overflowRef }) => {
  const plugins = useRef<PluginSpec[]>([
    [withHistory, {}],
    ImagesPlugin,
    LinksPlugin,
    HTMLCopyPastePlugin,
    RichTextPlugin,
    ListBreaksPlugin,
    OnelineHeadersPlugin,
    BlockSoftBreaksPlugin,
    [withReact, {}],
  ]);

  const uploadProvider = useAttachments();

  useEffect(() => {
    if (agentID) {
      uploadProvider.mergeTransaction({ agentID });
    }
    // eslint-disable-next-line
  }, []);

  const richEditor = useRef<Editor | null>(null);

  const richTextFromString = useRef(
    memoizeOne(
      (value: string | null) =>
        (value || "").split("\n").map(text => ({
          type: "paragraph",
          children: [{ text }],
        })) as Descendant[]
    )
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const signatureText = (value: Descendant[]) =>
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value.reduce((text, node): any => {
      if (Element.isElement(node)) {
        const childrenText = signatureText(node.children);
        // @ts-expect-error TS(2345)
        const isInline = Editor.isInline(richEditor.current, node);
        const isLink = node.type === "link";
        return `${text}${childrenText}${isLink ? ` (${(node as LinkElement).url})` : ""}${isInline ? "" : "\n"}`;
      }

      if (Text.isText(node)) {
        return `${text}${node.text}`;
      }

      return text;
    }, "");

  const [linkUpdateParams, setLinkUpdateParams] = useState<IUpdateParams | null>(null);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleLinkInsert = (event: any) => {
    const editor = richEditor.current;
    if (!editor) return;

    const [linkEntry] = Editor.nodes(editor, {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (node: any) => Element.isElement(node) && node.type === "link",
      universal: true,
    });

    let linkRange = null;
    if (linkEntry) {
      linkRange = Editor.range(editor, Editor.start(editor, linkEntry[1]), Editor.end(editor, linkEntry[1]));
      Transforms.select(editor, linkRange);
    }

    event.preventDefault();
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }

    if (editor.selection) {
      const domRange = ReactEditor.toDOMRange(editor, editor.selection);
      if (domRange?.startContainer?.parentElement) {
        scrollIntoView(domRange?.startContainer?.parentElement, { scrollMode: "if-needed" });
      }
    }

    setLinkUpdateParams({
      // @ts-expect-error TS(2345)
      selection: Editor.rangeRef(editor, editor.selection, { affinity: "inward" }),
      ...(linkRange ? { focusDisplayName: false } : { focusDisplayName: true }),
    });
  };

  const containerRef = useRef<HTMLElement | null>(null);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLinkHotkey = useCallback((event: any, pluginKeyDown: any) => {
    const isLinkHotkey = isHotkey("mod+k");

    if (isLinkHotkey(event.nativeEvent)) {
      handleLinkInsert(event);
    }

    pluginKeyDown(event);
  }, []);

  return (
    <AgentFormSection
      header={{
        id: AgentFormSectionId.Signature,
        component: (
          <>
            Signature
            {!canChange && <LockIcon />}
          </>
        ),
      }}
    >
      <Field
        id=""
        name="signature"
        disabled={!canChange}
        parse={
          // this ensures react-final-form never sets value to "undefined"
          // https://github.com/final-form/react-final-form/issues/130#issuecomment-425482365
          identity
        }
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render={(props: any) => (
          <div
            ref={c => {
              containerRef.current = c;
            }}
          >
            <SignatureFormField
              labelText="Signature is added at the end of messages sent by this agent."
              labelFor="rich-text-area"
            >
              <RichEditor
                // @ts-expect-error TS(2322)
                value={
                  (props.input.value as IAgent["signature"]).richTextObj === null
                    ? richTextFromString.current((props.input.value as IAgent["signature"]).text)
                    : (props.input.value as IAgent["signature"]).richTextObj
                }
                // FIXME: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onInit={(editor: any) => {
                  // eslint-disable-next-line no-param-reassign
                  editor.eventPlace = EventPlace.Tickets;
                  richEditor.current = editor;
                }}
                // FIXME: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(newValue: any) => {
                  if (richEditor.current) {
                    props.input.onChange({
                      target: { value: { text: signatureText(newValue), richTextObj: newValue } },
                    });
                  }
                }}
                plugins={plugins.current}
              >
                <SignatureTextAreaContainer
                  // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onClick={(event: any) => {
                    event.preventDefault();
                    if (richEditor.current) {
                      ReactEditor.focus(richEditor.current);
                    }
                  }}
                  disabled={!canChange}
                >
                  <LinkUI
                    params={linkUpdateParams}
                    setParams={setLinkUpdateParams}
                    containerRef={containerRef.current}
                    eventPlace={EventPlace.Agents}
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onInit={(positionUI: any) => {
                      if (overflowRef) {
                        overflowRef.addEventListener("scroll", positionUI);

                        return () => {
                          overflowRef.removeEventListener("scroll", positionUI);
                        };
                      }

                      return () => {};
                    }}
                  />
                  <RichTextArea
                    id="rich-text-area"
                    placeholder="Enter signature…"
                    readOnly={!canChange}
                    onKeyDown={handleLinkHotkey}
                  />
                </SignatureTextAreaContainer>
                <Toolbar disabled={!canChange} onLinkInsert={handleLinkInsert} />
              </RichEditor>
            </SignatureFormField>
          </div>
        )}
      />
    </AgentFormSection>
  );
};

const SignatureFormField = styled(FormField)`
  width: 100%;
`;

export default AgentSignature;
